import React from 'react';
import {
  HeadingThree,
  BodyTwoSecondary,
  SubtitleOne,
  GenesisTheme,
  useTheme,
} from '@leagueplatform/genesis-commons';
import { UtilityText, TextAction } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import {
  isAbsoluteUrl,
  formatPhoneNumber,
  normalizeUrl,
  defaultHandleLink,
} from '@leagueplatform/web-common';
import { CardBanner } from '@leagueplatform/web-common-components';
import { track } from '@leagueplatform/analytics';
import {
  useConfigProperty,
  useUnknownConfigProperty,
} from '@leagueplatform/core';
import { SOLUTION_TYPE } from '@leagueplatform/care-collections-api';
import { parseSolutionUrl } from '../../utils/parse-solution-url';

interface SolutionCardProps {
  title: string;
  description: string;
  url: string;
  image: string;
  isRecommended?: boolean;
  type: SOLUTION_TYPE;
  careCollectionId: string;
  topicTitle: string;
  phoneNumber?: string;
}

const getCardBannerStyles = ({ breakpoints }: GenesisTheme) => ({
  imageContainer: {
    minHeight: '56px',
    width: '56px',
    borderRadius: 'medium',
    alignSelf: 'center',
    flexShrink: '0',
    margin: 'one',
    marginInlineEnd: 'none',
    '&&': {
      [`@media screen and (min-width: ${breakpoints.phone})`]: {
        margin: 'two',
        height: '136px',
        maxWidth: '136px',
        borderInlineStartStyle: 'none',
      },
    },
  },
  wrapper: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
  },
});

export const SolutionCard = ({
  title,
  description,
  url,
  image,
  type,
  isRecommended = false,
  careCollectionId,
  topicTitle,
  phoneNumber,
}: SolutionCardProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();

  // TODO [CDSV-1429] Remove once color tokens are fixed for all tenants
  const adjustForCardColorThemeDiscrepancy = useUnknownConfigProperty(
    '__care__adjustForCardColorThemeDiscrepancy',
  );
  const backgroundColor = (() => {
    if (adjustForCardColorThemeDiscrepancy) {
      return isRecommended ? 'surface.background.secondary' : undefined;
    }
    return isRecommended ? 'surface.card.primary' : 'surface.card.secondary';
  })();
  const isExternalUrl = isAbsoluteUrl(url);
  const handleLinkCallback = useConfigProperty(
    'core.routing.handleExternalLink',
    defaultHandleLink,
  );
  return (
    <CardBanner
      url={phoneNumber ? undefined : parseSolutionUrl(normalizeUrl(url), type)}
      styles={getCardBannerStyles(theme)}
      image={image}
      backgroundColor={backgroundColor}
      onClick={(e) => {
        track(
          'care_collections.button_clicked.care_collection_item_screen_name.navigate_to_care_collections_solution',
          {
            resource_name: title.toLowerCase(),
            care_collection_id: careCollectionId,
            resource_type: type,
            screen_name: topicTitle,
            content_id: '',
            is_recommended_next_step: String(isRecommended),
            card_index: '',
            section_header: '',
            campaign_id: '',
            campaign_status: '',
          },
        );
        if (isExternalUrl) {
          e?.preventDefault();
          handleLinkCallback(url);
        }
      }}
    >
      {isRecommended && (
        <UtilityText
          as="p"
          css={{
            typography: '$overline',
            alignSelf: 'flex-start',
            backgroundColor: '$highlightBackgroundSubdued',
            width: 'fit-content',
            borderRadius: '$small',
            padding: '$quarter',
            marginBottom: '$threeQuarters',
            textTransform: 'uppercase',
          }}
        >
          {formatMessage({ id: 'RECOMMENDED_NEXT_STEP' })}
        </UtilityText>
      )}
      <HeadingThree marginBottom="half">{title}</HeadingThree>
      <BodyTwoSecondary>{description}</BodyTwoSecondary>
      {phoneNumber ? (
        <SubtitleOne as="h4" marginTop="one">
          {formatPhoneNumber(phoneNumber)}
        </SubtitleOne>
      ) : (
        <TextAction
          as="p"
          indicator="disclosure"
          size="medium"
          css={{
            marginTop: '$one',
            fontWeight: 'bold',
            '&:hover': {
              textDecoration: 'none',
            },
          }}
        >
          {formatMessage({ id: 'VIEW_THIS_RESOURCE' })}
        </TextAction>
      )}
    </CardBanner>
  );
};
